// Worker FSM + assignment solver + shelter + repair + worker production (plan §11).

import { buildingDef, unitDef } from '../../content/registry';
import { TUNING } from '../../content/tuning';
import type { PlayerId } from '../../content/types';
import { dist, moveToward, vec, type Vec2 } from '../../shared/vec';
import { dynamicEjectionPoint, hasOpenClearance, yieldWaypoint } from '../mobility';
import type { Sim } from '../sim';
import type { Building, Deposit, WorkerEnt } from '../state';
import { rootAuraMults } from './annexes';

const dt = TUNING.dt;

/**
 * Worker travel speed with Core Annex modifiers: retreat haste (Deeproot Sanctuary,
 * rooted Rootwardens) while fleeing/sheltering, conduit carry haste (Conduit Nexus)
 * while hauling near the connected network.
 */
function workerSpeed(sim: Sim, w: WorkerEnt): number {
  let speed = unitDef(w.defId).moveSpeed;
  if (w.state === 'fleeing' || w.state === 'toShelter') {
    speed *= sim.annexMult(w.owner, 'workerRetreatSpeedMult');
    speed *= rootAuraMults(sim, w).retreat;
  }
  if (w.carrying) {
    const haste = sim.annexMult(w.owner, 'conduitCarryHaste');
    if (haste !== 1) {
      for (const b of sim.state.buildings.values()) {
        if (b.owner !== w.owner || b.state !== 'active') continue;
        if (!buildingDef(b.defId).conduit) continue;
        if (!b.isCore && !sim.state.connectedConduits.has(b.id)) continue;
        if (dist(w.pos, b.pos) <= 14) {
          speed *= haste;
          break;
        }
      }
    }
  }
  return speed;
}

function abandonUnreachableTask(w: WorkerEnt): void {
  w.path = null;
  w.pathIndex = 0;
  w.stuckTime = 0;
  w.recoveries = 0;
  switch (w.state) {
    case 'toDeposit':
      w.depositId = null;
      w.state = 'idle';
      break;
    case 'toBuild':
      w.buildTargetId = null;
      w.state = 'idle';
      break;
    case 'toRepair':
      w.repairTargetId = null;
      w.state = 'idle';
      break;
    case 'toDropOff':
      // Keep the cargo; idle immediately re-solves a reachable drop-off next tick.
      w.state = 'idle';
      break;
    case 'toShelter':
      // Do not tunnel through an enemy gate to satisfy recall. Back off, then retry.
      w.state = 'fleeing';
      w.fleeTimer = Math.max(w.fleeTimer, 2);
      break;
    default:
      break;
  }
}

function followPath(sim: Sim, w: WorkerEnt, goal: Vec2, speed: number): boolean {
  // returns true when arrived (within 1.2 m of goal)
  if (dist(w.pos, goal) < 1.2) {
    w.stuckTime = 0;
    w.recoveries = 0;
    return true;
  }
  if (!w.path || w.path.length === 0) {
    w.path = sim.nav.findPath(w.pos, goal, w.owner);
    w.pathIndex = 0;
    if (!w.path) {
      w.stuckTime += dt;
      if (w.stuckTime >= 1.5) abandonUnreachableTask(w);
      return false;
    }
  }
  const wp = w.path[w.pathIndex];
  if (!wp) {
    w.path = null;
    return dist(w.pos, goal) < 1.5;
  }
  const next = moveToward(w.pos, wp, speed * dt);
  if (!sim.nav.isWalkable(next, w.owner)) {
    // Dynamic blockers can invalidate an already-issued route. Preserve no-progress time;
    // resetting it on every blocked retry made a shut gate an infinite worker trap.
    w.path = null;
    w.pathIndex = 0;
    w.stuckTime += dt;
    if (w.stuckTime >= 1) recoverStuckWorker(sim, w, goal);
    return false;
  }
  const beforeGoal = dist(w.pos, goal);
  w.pos = next;
  if (dist(w.pos, wp) < 0.3) w.pathIndex++;
  if (w.pathIndex >= w.path.length) w.path = null;
  const progressed = beforeGoal - dist(w.pos, goal) >= speed * dt * 0.08;
  w.stuckTime = progressed ? Math.max(0, w.stuckTime - dt * 3) : w.stuckTime + dt;
  if (progressed && w.stuckTime === 0) w.recoveries = 0; // sustained progress: healthy again
  if (w.stuckTime >= 1) {
    w.path = null;
    w.pathIndex = 0;
    recoverStuckWorker(sim, w, goal);
  }
  return false;
}

/**
 * Squad-style stuck recovery (movement.ts uses yieldWaypoint the same way): a deterministic
 * local side-step off whatever the worker is pinned against, so it can approach its goal
 * from a clear angle instead of repathing into the identical blocked step every tick.
 * Workers still never breach or cross a hostile gate — the side-step is physically probed.
 * One that keeps needing recovery without ever making net progress, or that cannot
 * side-step at all, abandons the task and re-solves instead of leaning on the obstacle.
 */
function recoverStuckWorker(sim: Sim, w: WorkerEnt, goal: Vec2): void {
  w.recoveries++;
  const sidestep = w.recoveries <= 4 ? yieldWaypoint(sim, w.id, w.pos, goal, w.owner) : null;
  if (sidestep) {
    w.path = [sidestep];
    w.pathIndex = 0;
    w.stuckTime = 0.35;
  } else {
    abandonUnreachableTask(w);
  }
}

function nearestEnemySquadDist(sim: Sim, pos: Vec2, owner: PlayerId): number {
  let best = Infinity;
  for (const s of sim.state.squads.values()) {
    if (!sim.areHostile(s.owner, owner)) continue;
    const d = dist(s.pos, pos);
    if (d < best) best = d;
  }
  return best;
}

/** Gather radius of a worker hub (Core territory or site build radius). */
function hubRadius(sim: Sim, hub: Building): number {
  return hub.isCore ? sim.map.coreTerritoryRadius : TUNING.buildRadius;
}

/** The zone a worker gathers in: around its home hub (Core territory or site build radius). */
function homeZone(sim: Sim, w: WorkerEnt): { center: Vec2; radius: number } | null {
  let hub = w.homeId !== null ? sim.state.buildings.get(w.homeId) : undefined;
  if (!hub || hub.state !== 'active') {
    // hub gone: adopt the nearest one (usually the Core)
    hub = sim.nearestWorkerHub(w.owner, w.pos) ?? undefined;
    w.homeId = hub?.id ?? null;
  }
  if (!hub) return null;
  return {
    center: hub.pos,
    radius: hubRadius(sim, hub),
  };
}

/** Nearest owned hub whose zone still holds a live deposit (ascending distance, id tie-break). */
function nearestProductiveHub(sim: Sim, w: WorkerEnt): Building | null {
  let best: Building | null = null;
  let bestD = Infinity;
  for (const hub of sim.workerHubs(w.owner)) {
    const radius = hubRadius(sim, hub);
    let hasDeposit = false;
    for (const dep of sim.state.deposits.values()) {
      if (
        dep.amount > 0 &&
        dist(dep.pos, hub.pos) <= radius &&
        sim.nav.canReach(w.pos, dep.pos, w.owner)
      ) {
        hasDeposit = true;
        break;
      }
    }
    if (!hasDeposit) continue;
    const d = dist(w.pos, hub.pos);
    if (d < bestD - 1e-9 || (best !== null && Math.abs(d - bestD) <= 1e-9 && hub.id < best.id)) {
      bestD = d;
      best = hub;
    }
  }
  return best;
}

/** Is this worker's home hub recalling its zone to shelter? */
function hubRecalling(sim: Sim, w: WorkerEnt): boolean {
  const hub = w.homeId !== null ? sim.state.buildings.get(w.homeId) : undefined;
  return hub?.recallActive ?? false;
}

/**
 * Deterministic over-capacity check: workers sharing a hub rank by ascending id; ranks at or
 * beyond the zone cap gather at half rate (owner request — excess workers still work).
 */
function computeOverCap(sim: Sim, w: WorkerEnt): boolean {
  const hub = w.homeId !== null ? sim.state.buildings.get(w.homeId) : undefined;
  if (!hub) return false;
  const cap = sim.zoneWorkerCap(w.owner, hub);
  let rank = 0;
  for (const o of sim.state.workers.values()) {
    if (o.owner === w.owner && !o.dead && o.homeId === w.homeId && o.id < w.id) rank++;
  }
  return rank >= cap;
}

/** Score deposits for one worker per §11.2, restricted to the worker's home zone. */
function pickDeposit(sim: Sim, w: WorkerEnt): Deposit | null {
  const p = sim.state.players[w.owner];
  const zone = homeZone(sim, w);
  // current split of assigned workers
  let powerWorkers = 0;
  let essenceWorkers = 0;
  for (const o of sim.state.workers.values()) {
    if (o.owner !== w.owner || o.id === w.id || o.depositId === null) continue;
    const dep = sim.state.deposits.get(o.depositId);
    if (!dep) continue;
    if (dep.type === 'power') powerWorkers++;
    else essenceWorkers++;
  }
  const total = powerWorkers + essenceWorkers + 1;
  const targetPowerShare = p.allocationPower / 100;

  // occupancy per deposit
  const occupancy = new Map<number, number>();
  for (const o of sim.state.workers.values()) {
    if (o.owner !== w.owner || o.depositId === null || o.id === w.id) continue;
    occupancy.set(o.depositId, (occupancy.get(o.depositId) ?? 0) + 1);
  }

  let best: Deposit | null = null;
  let bestScore = 0;
  let sawEssence = false;
  for (const dep of sim.state.deposits.values()) {
    if (dep.amount <= 0) continue;
    // zone assignment (owner feedback): a worker only gathers inside its home zone
    if (zone && dist(dep.pos, zone.center) > zone.radius) continue;
    if (!sim.nav.canReach(w.pos, dep.pos, w.owner)) continue;
    if (dep.type === 'essence') sawEssence = true;
    const shareIfAssigned =
      dep.type === 'power' ? (powerWorkers + 1) / total : 1 - (essenceWorkers + 1) / total;
    // ratioNeed: how much does assigning here move us toward the target split
    const currentPowerShare = powerWorkers / total;
    const need =
      dep.type === 'power'
        ? Math.max(0.05, targetPowerShare - currentPowerShare + 0.5)
        : Math.max(0.05, currentPowerShare - targetPowerShare + 0.5 + (1 - targetPowerShare) * 0.2);
    void shareIfAssigned;
    const d = dist(w.pos, dep.pos);
    const distanceFactor = 1 / (1 + d / 40);
    const occ = occupancy.get(dep.id) ?? 0;
    const slotFactor = occ >= dep.gatherSlots + 2 ? 0 : 1 - (occ / dep.gatherSlots) * 0.5;
    const dangerFactor = nearestEnemySquadDist(sim, dep.pos, w.owner) < TUNING.solverDangerRadius ? 0.2 : 1;
    const depletionFactor = dep.amount < dep.initialAmount * 0.1 ? 0.5 : 1;
    const score = need * distanceFactor * slotFactor * dangerFactor * depletionFactor;
    if (score > bestScore + 1e-9 || (score > 0 && best !== null && Math.abs(score - bestScore) <= 1e-9 && dep.id < best.id)) {
      bestScore = score;
      best = dep;
    }
  }
  p.noEssenceWarning = !sawEssence && p.allocationPower < 100;
  // home zone dry: relocate to the nearest owned zone that still has resources (owner
  // request — workers should almost never idle while any owned zone can be worked)
  if (!best) {
    const hub = nearestProductiveHub(sim, w);
    if (hub && hub.id !== w.homeId) {
      w.homeId = hub.id;
      return pickDeposit(sim, w); // depth ≤ 2: the adopted zone is known to hold a deposit
    }
  }
  return best;
}

function dropOffTarget(sim: Sim, w: WorkerEnt): Building | null {
  // nearest of: own Core, own site towers, and drop-off buildings matching the carried type
  let best: Building | null = null;
  let bestD = Infinity;
  const core = sim.coreAlive(w.owner);
  if (core) {
    const cd = buildingDef(core.defId);
    const doorway = vec(core.pos.x, core.pos.y - cd.footprint.h / 2 - 1);
    if (sim.nav.canReach(w.pos, doorway, w.owner)) {
      best = core;
      bestD = dist(w.pos, doorway);
    }
  }
  for (const b of sim.state.buildings.values()) {
    if (b.owner !== w.owner || b.state !== 'active') continue;
    const bd = buildingDef(b.defId);
    const takesCargo =
      bd.isSiteTower || (w.carrying !== null && (bd.dropOff?.includes(w.carrying.type) ?? false));
    if (!takesCargo) continue;
    const doorway = vec(b.pos.x, b.pos.y - bd.footprint.h / 2 - 1);
    if (!sim.nav.canReach(w.pos, doorway, w.owner)) continue;
    const d = dist(w.pos, doorway);
    if (d < bestD) {
      bestD = d;
      best = b;
    }
  }
  return best;
}

export function stepWorkers(sim: Sim): void {
  const st = sim.state;

  // ---- worker production queues
  for (const pid of sim.playerIds()) {
    const p = st.players[pid];
    const core = sim.coreAlive(pid);
    // auto-production: keep one queued (bounded by the Core zone's worker cap)
    if (
      p.autoWorker &&
      core &&
      !core.recallActive &&
      p.workerQueue.length === 0 &&
      p.workforceUsed < p.workforceCap &&
      sim.zoneHasWorkerRoom(pid, core) &&
      p.power >= TUNING.workerCost
    ) {
      p.power -= TUNING.workerCost;
      p.workerQueue.push({ remaining: TUNING.workerTrainTime });
      sim.recomputeCaps(pid);
    }
    if (p.workerQueue.length > 0 && core) {
      const item = p.workerQueue[0];
      item.remaining -= dt * sim.annexMult(pid, 'workerTrainSpeedMult'); // Ossuary Ledger
      if (item.remaining <= 0) {
        p.workerQueue.shift();
        const d = buildingDef(core.defId);
        sim.spawnWorker(pid, vec(core.pos.x, core.pos.y - d.footprint.h / 2 - 1.5), undefined, core.id);
      }
    }
    // site towers train their own workers, homed to that zone (per-zone cap)
    for (const b of st.buildings.values()) {
      if (b.owner !== pid || b.state !== 'active') continue;
      const bd = buildingDef(b.defId);
      if (!bd.producesWorkers) continue;
      if (b.workerQueueRemaining === null) {
        if (
          b.workerAuto &&
          !b.recallActive &&
          p.workforceUsed < p.workforceCap &&
          sim.zoneHasWorkerRoom(pid, b) &&
          p.power >= TUNING.workerCost
        ) {
          p.power -= TUNING.workerCost;
          b.workerQueueRemaining = TUNING.workerTrainTime;
          sim.recomputeCaps(pid);
        }
      } else {
        b.workerQueueRemaining -= dt * sim.annexMult(pid, 'workerTrainSpeedMult'); // Ossuary Ledger
        if (b.workerQueueRemaining <= 0) {
          b.workerQueueRemaining = null;
          sim.spawnWorker(pid, vec(b.pos.x, b.pos.y - bd.footprint.h / 2 - 1.2), undefined, b.id);
        }
      }
    }
    // shelter staggered release — a worker leaves once ITS hub is no longer recalling
    if (p.shelterOccupants.length > 0 && core) {
      p.shelterReleaseTimer -= dt;
      if (p.shelterReleaseTimer <= 0) {
        p.shelterReleaseTimer = TUNING.shelterReleaseInterval;
        const idx = p.shelterOccupants.findIndex((id) => {
          const w = st.workers.get(id);
          if (!w) return true; // stale entry — flush it
          return !hubRecalling(sim, w);
        });
        if (idx !== -1) {
          const id = p.shelterOccupants.splice(idx, 1)[0];
          const w = st.workers.get(id);
          if (w) {
            const d = buildingDef(core.defId);
            w.state = 'idle';
            w.pos = sim.nav.nearestWalkable(vec(core.pos.x + 2, core.pos.y - d.footprint.h / 2 - 1.5));
            w.prevPos = { ...w.pos };
            w.lastOpenPos = { ...w.pos };
            w.stuckTime = 0;
          }
        }
      }
    }
  }

  // repairers per building this tick (for diminishing returns ordering)
  const repairerIndex = new Map<number, number>();

  for (const w of [...st.workers.values()]) {
    if (w.dead) continue;
    if (hasOpenClearance(sim, w.pos, w.owner)) w.lastOpenPos = { ...w.pos };
    const p = st.players[w.owner];
    const core = sim.coreAlive(w.owner);

    // Core Annex channels: transfer materialization and Deep Recall burrowing hold
    // the worker in place (timers advance in annexes.ts)
    if (w.arrivalRemaining > 0 || w.recallChannelRemaining > 0) continue;

    // flee reflex: recently damaged, or armed enemy squad very near — unless sheltering
    if (w.state !== 'toShelter' && w.state !== 'sheltered' && w.state !== 'fleeing') {
      const recentlyHit = sim.state.time - w.lastDamagedAt < 1;
      if (recentlyHit || nearestEnemySquadDist(sim, w.pos, w.owner) < TUNING.workerFleeRadius) {
        w.state = 'fleeing';
        w.fleeTimer = TUNING.workerFleeSeconds;
        w.path = null;
      }
    }

    switch (w.state) {
      case 'idle': {
        if (hubRecalling(sim, w)) {
          w.state = 'toShelter';
          break;
        }
        if (w.carrying) {
          w.state = 'toDropOff';
          w.path = null;
          break;
        }
        const dep = pickDeposit(sim, w);
        if (dep) {
          w.depositId = dep.id;
          w.overCap = computeOverCap(sim, w); // trip-boundary rank (may have moved zones)
          w.state = 'toDeposit';
          w.path = null;
        }
        break;
      }
      case 'toDeposit': {
        const dep = w.depositId !== null ? st.deposits.get(w.depositId) : undefined;
        if (!dep || dep.amount <= 0) {
          w.depositId = null;
          w.state = 'idle';
          break;
        }
        if (followPath(sim, w, dep.pos, workerSpeed(sim, w))) {
          w.state = 'gathering';
          w.gatherTimer = TUNING.gatherChannelSeconds;
        }
        break;
      }
      case 'gathering': {
        const dep = w.depositId !== null ? st.deposits.get(w.depositId) : undefined;
        if (!dep || dep.amount <= 0) {
          w.depositId = null;
          w.state = w.carrying ? 'toDropOff' : 'idle';
          break;
        }
        // Grim Industry stacks and rooted Rootwarden auras speed the gather channel
        w.gatherTimer -= dt * sim.grimIndustryMult(w.owner) * rootAuraMults(sim, w).gather;
        if (w.gatherTimer <= 0) {
          let carryMult = unitDef(w.defId).workerVariant?.carryMult ?? 1;
          carryMult *= sim.annexMult(w.owner, 'carryMult'); // Deeproot Sanctuary
          if (w.overCap) carryMult *= TUNING.overCapacityRateMult; // excess workers: half rate
          const amt = Math.max(1, Math.min(Math.round(TUNING.carryCapacity * carryMult), dep.amount));
          // deposit preservation (Living Grove): the strongest nearby effect slows
          // depletion — the worker still carries the full load
          let drainMult = 1;
          for (const b of st.buildings.values()) {
            if (b.owner !== w.owner || b.state !== 'active') continue;
            const passive = buildingDef(b.defId).passive;
            if (passive?.kind !== 'depositPreservation') continue;
            if (dist(b.pos, dep.pos) <= passive.radius) drainMult = Math.min(drainMult, passive.drainMult);
          }
          dep.amount -= amt * drainMult;
          if (dep.amount <= 0) st.deposits.delete(dep.id);
          w.carrying = { type: dep.type, amount: amt };
          w.state = 'toDropOff';
          w.path = null;
        }
        break;
      }
      case 'toDropOff': {
        const drop = dropOffTarget(sim, w);
        if (!drop) {
          w.stuckTime += dt;
          if (w.stuckTime >= 1.5) abandonUnreachableTask(w);
          break;
        }
        const d = buildingDef(drop.defId);
        const doorway = vec(drop.pos.x, drop.pos.y - d.footprint.h / 2 - 1);
        if (followPath(sim, w, doorway, workerSpeed(sim, w))) {
          if (w.carrying) {
            if (w.carrying.type === 'power') {
              p.power += w.carrying.amount;
              p.stats.powerGathered += w.carrying.amount;
            } else {
              p.essence += w.carrying.amount;
              p.stats.essenceGathered += w.carrying.amount;
            }
            w.carrying = null;
          }
          // lazy reassignment (A-15): re-evaluate at trip boundary
          w.state = 'idle';
          w.path = null;
        }
        break;
      }
      case 'toBuild': {
        const b = w.buildTargetId !== null ? st.buildings.get(w.buildTargetId) : undefined;
        if (!b || (b.state !== 'blueprint' && b.state !== 'constructing')) {
          w.buildTargetId = null;
          w.state = 'idle';
          break;
        }
        const d = buildingDef(b.defId);
        const spot = vec(b.pos.x, b.pos.y - d.footprint.h / 2 - 1);
        if (followPath(sim, w, spot, workerSpeed(sim, w))) w.state = 'building';
        break;
      }
      case 'building': {
        const b = w.buildTargetId !== null ? st.buildings.get(w.buildTargetId) : undefined;
        if (!b || b.state === 'active' || b.state === 'destroyed') {
          w.buildTargetId = null;
          w.state = 'idle';
        }
        // progress applied by construction system (counts builders per building)
        break;
      }
      case 'toRepair': {
        const b = w.repairTargetId !== null ? st.buildings.get(w.repairTargetId) : undefined;
        if (!b || b.state === 'destroyed' || b.health >= b.maxHealth) {
          w.repairTargetId = null;
          w.state = 'idle';
          break;
        }
        const d = buildingDef(b.defId);
        const spot = vec(b.pos.x, b.pos.y - d.footprint.h / 2 - 1);
        if (followPath(sim, w, spot, workerSpeed(sim, w))) w.state = 'repairing';
        break;
      }
      case 'repairing': {
        const b = w.repairTargetId !== null ? st.buildings.get(w.repairTargetId) : undefined;
        if (!b || b.state === 'destroyed' || b.health >= b.maxHealth) {
          w.repairTargetId = null;
          w.state = 'idle';
          break;
        }
        // ordering index for diminishing returns
        const idx = repairerIndex.get(b.id) ?? 0;
        repairerIndex.set(b.id, idx + 1);
        // core assault cap (A-12)
        const underAssault = b.isCore && sim.state.time - b.lastDamagedAt < TUNING.coreAssaultWindowSeconds;
        if (underAssault && idx >= TUNING.coreAssaultRepairWorkerCap) break;
        if (idx >= TUNING.repairMaxWorkers) break;
        let rate = TUNING.repairHpPerSecond * (TUNING.repairDiminish[idx] ?? 0.25);
        rate *= sim.annexMult(w.owner, 'repairSpeedMult');
        rate *= unitDef(w.defId).workerVariant?.repairMult ?? 1;
        // −50% while damaged in the last 2 s
        if (sim.state.time - b.lastDamagedAt < 2) rate *= 0.5;
        rate *= 1 - b.repairResist;
        if (b.repairHalvedRemaining > 0) rate *= 0.5; // Kingbreaker Hammer
        const heal = Math.min(rate * dt, b.maxHealth - b.health);
        const cost = (heal / 4) * TUNING.repairPowerPer4Hp;
        if (p.power >= cost && heal > 0) {
          p.power -= cost;
          b.health += heal;
        }
        break;
      }
      case 'fleeing': {
        w.fleeTimer -= dt;
        if (core) {
          const d = buildingDef(core.defId);
          followPath(sim, w, vec(core.pos.x, core.pos.y - d.footprint.h / 2 - 1), workerSpeed(sim, w) * 1.1);
        }
        if (w.fleeTimer <= 0) {
          w.state = hubRecalling(sim, w) ? 'toShelter' : 'idle';
          w.path = null;
        }
        break;
      }
      case 'toShelter': {
        if (!core) {
          w.state = 'idle';
          break;
        }
        // bank carried resources on the way in (§11.8.6)
        const d = buildingDef(core.defId);
        if (followPath(sim, w, vec(core.pos.x, core.pos.y - d.footprint.h / 2 - 1), workerSpeed(sim, w))) {
          if (w.carrying) {
            if (w.carrying.type === 'power') p.power += w.carrying.amount;
            else p.essence += w.carrying.amount;
            w.carrying = null;
          }
          if (p.shelterOccupants.length < p.shelterCapacity) {
            p.shelterOccupants.push(w.id);
            w.state = 'sheltered';
          } else {
            // overflow: hold at rear rally
            w.state = 'fleeing';
            w.fleeTimer = 2;
          }
        }
        break;
      }
      case 'sheltered':
        // untargetable, handled by release logic above
        break;
    }

    if (w.state !== 'sheltered') {
      if (!sim.nav.isWalkable(w.pos, w.owner)) {
        const exit = dynamicEjectionPoint(sim, w.pos, w.lastOpenPos, w.owner);
        if (exit) {
          w.pos = exit;
          w.prevPos = { ...exit };
          w.path = null;
          w.pathIndex = 0;
          w.stuckTime = 0;
        }
      } else if (hasOpenClearance(sim, w.pos, w.owner)) {
        w.lastOpenPos = { ...w.pos };
      }
    }
  }
}

/** Count idle workers for the HUD. */
export function idleWorkerCount(sim: Sim, owner: PlayerId): number {
  let n = 0;
  for (const w of sim.state.workers.values()) {
    if (w.owner === owner && w.state === 'idle') n++;
  }
  return n;
}
